import {
  ArrowTopRightOnSquareIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/solid"
import { Skeleton } from "components/new-stuff/ui/Skeleton"
import { MouseEvent, useCallback, useEffect, useRef, useState } from "react"
import { IFeatureAnnouncement } from "services/featureAnnouncementsService"

type VideoSource =
  | { kind: "embed"; src: string }
  | { kind: "file"; src: string }
  | { kind: "link"; href: string }

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

/**
 * Turns a pasted video link into something the popup can play. Providers that allow
 * embedding get their player, muted so the browser lets it autoplay; direct files get
 * our own player; Google Drive gets its preview player (click to play); anything else
 * (Dropbox, a web page) cannot be embedded reliably, so it becomes a link out.
 */
export const parseVideoUrl = (
  value: string,
  autoplay: boolean
): VideoSource | null => {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return null
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null

  const host = url.hostname.replace(/^www\.|^m\./, "")
  const play = autoplay ? "1" : "0"

  if (host === "youtube.com" || host === "youtu.be") {
    const id =
      host === "youtu.be"
        ? url.pathname.slice(1)
        : url.searchParams.get("v") ??
          url.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1]
    if (id && /^[\w-]{6,}$/.test(id)) {
      return {
        kind: "embed",
        src: `https://www.youtube-nocookie.com/embed/${id}?autoplay=${play}&mute=1&playsinline=1&rel=0&modestbranding=1`,
      }
    }
  }

  if (host === "vimeo.com" || host === "player.vimeo.com") {
    // vimeo.com/123 or vimeo.com/123/abcHash (unlisted); player URLs carry ?h=.
    const match = url.pathname.match(/(?:\/video)?\/(\d+)(?:\/([\da-f]+))?/)
    if (match) {
      const hash = match[2] ?? url.searchParams.get("h")
      return {
        kind: "embed",
        src: `https://player.vimeo.com/video/${match[1]}?autoplay=${play}&muted=1${
          hash ? `&h=${hash}` : ""
        }`,
      }
    }
  }

  if (host === "loom.com") {
    const id = url.pathname.match(/^\/(?:share|embed)\/([\da-f]+)/)?.[1]
    if (id) {
      return {
        kind: "embed",
        src: `https://www.loom.com/embed/${id}?autoplay=${play}&muted=1&hideEmbedTopBar=true`,
      }
    }
  }

  if (host === "drive.google.com") {
    // /file/d/<id>/view (also under /u/0/) or /open?id=<id>. Only /preview may be framed.
    // Drive's player takes no autoplay option, so these play when clicked, and only for
    // files shared with "Anyone with the link".
    const id =
      url.pathname.match(/\/file\/d\/([\w-]+)/)?.[1] ??
      url.searchParams.get("id")
    if (id && /^[\w-]{10,}$/.test(id)) {
      return {
        kind: "embed",
        src: `https://drive.google.com/file/d/${id}/preview`,
      }
    }
  }

  if (/\.(mp4|webm|m4v|mov|ogv)$/i.test(url.pathname)) {
    return { kind: "file", src: url.toString() }
  }

  return { kind: "link", href: url.toString() }
}

const formatDuration = (seconds: number) => {
  const total = Math.round(seconds)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}

/** Shown when there is neither a playable video nor an image. */
const Placeholder = () => (
  <svg
    viewBox="0 0 320 160"
    className="h-full w-full"
    aria-hidden="true"
  >
    <rect
      x="40"
      y="12"
      width="240"
      height="148"
      rx="10"
      fill="#fff"
      stroke="#E6EAF2"
    />
    {[56, 126, 196].map((x, index) => (
      <g key={x}>
        <rect
          x={x}
          y="32"
          width="60"
          height="30"
          rx="6"
          fill="#F5F7FB"
        />
        <rect
          x={x + 8}
          y="40"
          width="22"
          height="4"
          rx="2"
          fill={index === 0 ? "#93A8F4" : "#D5DBE6"}
        />
        <rect
          x={x + 8}
          y="50"
          width="32"
          height="6"
          rx="3"
          fill={index === 0 ? "#6D86F0" : "#C5CCD9"}
        />
      </g>
    ))}
    <rect
      x="56"
      y="74"
      width="208"
      height="70"
      rx="6"
      fill="#F5F7FB"
    />
    {[24, 34, 44, 16, 22].map((height, index) => (
      <rect
        key={index}
        x={70 + index * 18}
        y={134 - height}
        width="10"
        height={height}
        rx="2"
        fill={index === 2 ? "#6D86F0" : "#BFCDF8"}
      />
    ))}
  </svg>
)

/**
 * An announcement image that holds its frame while it downloads: the shared shimmer
 * until it lands, then a fade in. It fills its parent, so the parent sets the size. A
 * failed load calls `onError` so the caller can fall back.
 */
export const AnnouncementImage = ({
  src,
  onError,
}: {
  src: string
  onError?: () => void
}) => {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
  const loaded = loadedSrc === src

  return (
    <div
      className="relative h-full w-full"
      aria-busy={!loaded}
    >
      {!loaded && (
        <Skeleton
          variant="block"
          className="absolute inset-0 !rounded-none"
        />
      )}
      <img
        src={src}
        alt=""
        onLoad={() => setLoadedSrc(src)}
        onError={onError}
        className={`h-full w-full object-cover transition-opacity duration-300 motion-reduce:transition-none ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  )
}

const Still = ({ item }: { item: IFeatureAnnouncement }) => {
  // A broken image falls back to the placeholder rather than an empty frame.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const src = item.image_url !== failedSrc ? item.image_url : null

  return src ? (
    <AnnouncementImage
      src={src}
      onError={() => setFailedSrc(src)}
    />
  ) : (
    <Placeholder />
  )
}

/**
 * Our own player for direct files: autoplays muted (the only way browsers allow it),
 * with the design's demo length, and a progress strip and speaker toggle laid over
 * the bottom edge so the video stays edge to edge.
 */
const FilePlayer = ({
  src,
  poster,
  autoplay,
  onError,
  onPlayingChange,
  onEngage,
}: {
  src: string
  poster?: string | null
  autoplay: boolean
  onError: () => void
  onPlayingChange: (playing: boolean) => void
  /** The viewer chose to watch: pressed play, seeked or unmuted. Autoplay is not this. */
  onEngage?: () => void
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(true)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => onPlayingChange(playing), [playing, onPlayingChange])

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      onEngage?.()
      void video.play().catch(() => setPlaying(false))
    } else video.pause()
  }

  const seek = (event: MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current
    if (!video || !duration) return
    onEngage?.()
    const rect = event.currentTarget.getBoundingClientRect()
    video.currentTime = ((event.clientX - rect.left) / rect.width) * duration
  }

  return (
    <div className="relative h-full w-full bg-black">
        <video
          ref={videoRef}
          src={src}
          poster={poster ?? undefined}
          autoPlay={autoplay}
          muted={muted}
          playsInline
          preload="metadata"
          onClick={togglePlay}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onError={onError}
          onLoadedMetadata={(event) =>
            setDuration(event.currentTarget.duration || 0)
          }
          onTimeUpdate={(event) => {
            const video = event.currentTarget
            setProgress(video.duration ? video.currentTime / video.duration : 0)
          }}
          className="h-full w-full cursor-pointer object-contain"
        />
        {!playing && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label="Play demo"
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 focus:outline-none"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary500 text-white shadow-lg">
              <PlayIcon className="ml-1 w-7" />
            </span>
            {duration > 0 && (
              <span className="flex items-center gap-2 rounded-full bg-neutral700/80 px-3 py-1 text-sm font-medium text-white">
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#F5B83D]">
                  <PlayIcon className="ml-px w-2 text-neutral800" />
                </span>
                {formatDuration(duration)} min demo
              </span>
            )}
          </button>
        )}
      <button
        type="button"
        onClick={() => {
          if (muted) onEngage?.()
          setMuted((value) => !value)
        }}
        aria-label={muted ? "Unmute" : "Mute"}
        className="absolute bottom-4 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
          {muted ? (
            <SpeakerXMarkIcon className="w-5" />
          ) : (
            <SpeakerWaveIcon className="w-5" />
          )}
      </button>
      {/* A taller hit area than the strip it draws, so it is easy to click to seek. */}
      <div
        role="progressbar"
        aria-label="Video progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        onClick={seek}
        className="absolute inset-x-0 bottom-0 flex h-3 cursor-pointer items-end"
      >
        <div className="h-1 w-full bg-white/30">
          <div
            className="h-full bg-primary500"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * The popup's media area: the video when the link can play here, otherwise the image,
 * otherwise a placeholder. Mount it keyed per slide, so leaving a slide stops its video.
 *
 * `onPausedChange` tells the popup when to keep its chip and close button on screen.
 * Only our own player can say; embedded players do not tell this page, so they count
 * as playing (hover only). An image has nothing playing, so it counts as paused.
 */
export const AnnouncementMedia = ({
  item,
  onPausedChange,
  onEngage,
  autoplay: allowAutoplay = true,
}: {
  item: IFeatureAnnouncement
  onPausedChange?: (paused: boolean) => void
  /**
   * The viewer chose to watch, which marks the announcement seen. Our own player and
   * the Watch demo link can tell; embedded players (YouTube, Vimeo, Loom, Drive) do not
   * report plays to this page, so they never call it.
   */
  onEngage?: () => void
  /** The popup autoplays (muted); the Read more drawer waits for the viewer. */
  autoplay?: boolean
}) => {
  const [autoplay] = useState(() => allowAutoplay && !prefersReducedMotion())
  const source = item.video_url ? parseVideoUrl(item.video_url, autoplay) : null

  // A broken file falls back to the still rather than an empty player.
  const [failed, setFailed] = useState(false)

  const isEmbed = source?.kind === "embed"
  const isFile = source?.kind === "file" && !failed
  useEffect(() => {
    if (isEmbed) onPausedChange?.(false)
    else if (!isFile) onPausedChange?.(true)
  }, [isEmbed, isFile, onPausedChange])

  const handlePlayingChange = useCallback(
    (playing: boolean) => onPausedChange?.(!playing),
    [onPausedChange]
  )

  if (source?.kind === "embed") {
    return (
      <iframe
        src={source.src}
        title={item.title}
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        loading="lazy"
        className="h-full w-full border-0 bg-black"
      />
    )
  }

  if (source?.kind === "file" && !failed) {
    return (
      <FilePlayer
        src={source.src}
        poster={item.image_url}
        autoplay={autoplay}
        onError={() => setFailed(true)}
        onPlayingChange={handlePlayingChange}
        onEngage={onEngage}
      />
    )
  }

  return (
    <div className="relative h-full">
      <Still item={item} />
      {source?.kind === "link" && (
        <a
          href={source.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onEngage?.()}
          className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-primary500 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:opacity-90"
        >
          Watch demo
          <ArrowTopRightOnSquareIcon className="w-4" />
        </a>
      )}
    </div>
  )
}
